import { Button, Select } from "antd";
import { ColumnsType } from "antd/es/table";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAppSelector } from "../../../app/store";
import AddIcon from "../../../assets/icons/AddIcon";
import SelectDownIcon from "../../../assets/icons/SelectDownIcon";
import ViewIcon from "../../../assets/icons/ViewIcon";
import InterviewDetails from "../../../components/interview/InterviewDetails";
import ConfirmModal from "../../../components/ui/ConfirmModal";
import CustomTable from "../../../components/ui/CustomTable";
import {
  useGetAllJobPostQuery,
  useGetInterviewListQuery,
  useInterviewBulkStatusMutation,
} from "../../../features/employee/employeeApi";
import useError from "../../../hooks/useError";
import useModal from "../../../hooks/useModal";
import useTable from "../../../hooks/useTable";
import SidebarLayout from "../../../layouts/SidebarLayout";
import { toast } from "../../../lib/toast";
import { GetInterviewListParamsType, TInterview } from "../../../types";
import { countParPageSerialNumber } from "../../../utils/number";
import { capitalize } from "../../../utils/string";
import {
  addKeyInArray,
  selectSearchOption,
  selectStringOption,
} from "../../../utils/utils";

enum Status {
  pending = "status-warning",
  completed = "status-success",
  cancelled = "status-danger",
}

enum InterviewStatus {
  offered = "status-info",
  rejected = "status-dark",
}

export enum IType {
  Onsite = "Onsite",
  "Google Meet" = "Google Meet",
}

type TApplicationDetails = {
  details: string;
  cv: string;
  title: string;
  id: number;
  status: TInterview["status"];
  offerStatus: TInterview["offer_status"];
  email?: string;
  phone?: string;
  address?: string;
};

export default function InterviewList() {
  const { authInfo } = useAppSelector((state) => state.auth);
  const [selectedData, setSelectedData] = useState<string[]>([]);
  const { open, handleOpen, handleClose } = useModal<
    TInterview["offer_status"] | TApplicationDetails | undefined,
    "CONFIRM_BULK_ACTION"
  >();

  const { page, limit, handlePagination, handleLimit, filter, handleFilter } =
    useTable<GetInterviewListParamsType>({
      defaultFilter: {
        business_id: authInfo?.business_id!,
      },
    });
  const {
    isLoading: isJobLoading,
    isError: isJobError,
    error: jobError,
    data: jobData,
  } = useGetAllJobPostQuery({
    params: { skip: 0, limit: 0, business_id: authInfo?.business_id! },
  });
  const { isLoading, isError, error, data } = useGetInterviewListQuery({
    params: filter,
  });
  const [
    interviewBulkStatus,
    {
      isLoading: isBulkLoading,
      isError: isBulkError,
      error: bulkError,
      isSuccess: isBulkSuccess,
    },
  ] = useInterviewBulkStatusMutation();

  useError(isError, error, "Get interview list fail");
  useError(isJobError, jobError, "Get job post data fail");
  useError(isBulkError, bulkError, "Interview status update fail");

  const columns: ColumnsType<TInterview> = [
    {
      title: "#SL",
      align: "center",
      width: 60,
      render: (_, __, index) => countParPageSerialNumber(page, limit, index),
    },
    {
      title: "Title",
      dataIndex: "job_post",
      render: (value) => value?.job_title || "n/a",
    },
    {
      title: "Applicant Name",
      dataIndex: "job_application",
      render: (value) => value?.full_name || "n/a",
    },
    {
      title: "Contact No.",
      dataIndex: "job_application",
      render: (value) => value?.mobile || "n/a",
    },
    {
      title: "Email",
      dataIndex: "job_application",
      render: (value) => value?.email || "n/a",
    },
    {
      title: "Interview Time",
      dataIndex: "interview_date",
      render: (value, record) =>
        value ? `${value} ${record?.interview_time}` : "n/a",
    },
    {
      title: "Interview Type",
      dataIndex: "interview_type",
      render: (value) => value || "n/a",
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (val: keyof typeof Status) => (
        <span className={Status[val]}>{capitalize(val)}</span>
      ),
    },
    {
      title: "Interview Status",
      dataIndex: "offer_status",
      render: (val: keyof typeof InterviewStatus) => (
        <span className={InterviewStatus[val]}>{capitalize(val || "-")}</span>
      ),
    },
    {
      title: "Action",
      dataIndex: "id",
      align: "center",
      render: (val, record) => {
        return (
          <Button
            className="action-button"
            size="small"
            icon={<ViewIcon />}
            onClick={() => {
              handleOpen({
                type: "VIEW",
                data: {
                  details: record?.job_application?.inquiry,
                  cv: record?.job_application?.cv_link,
                  title: record?.job_application?.full_name,
                  id: val,
                  status: record?.status,
                  offerStatus: record?.offer_status,
                  email: record?.job_application?.email,
                  phone: record?.job_application?.mobile,
                },
              });
            }}
          />
        );
      },
    },
  ];

  const handleBulkStatus = () => {
    interviewBulkStatus({
      ids: selectedData,
      status: open?.data,
    });
  };

  useEffect(() => {
    if (isBulkSuccess) {
      toast(
        "success",
        open?.data === ("completed" as TInterview["offer_status"])
          ? "Interview successfully completed"
          : "Interview successfully cancelled"
      );
      setSelectedData([]);
      handleClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isBulkSuccess]);

  return (
    <SidebarLayout title="Interview List">
      {open?.type === "CONFIRM_BULK_ACTION" && (
        <ConfirmModal
          open={open?.open}
          handleOk={handleBulkStatus}
          handleClose={handleClose}
          okText="Confirm"
          confirmTitle={"Confirmation"}
          questionText={`${
            open?.data === ("completed" as TInterview["offer_status"])
              ? "Are you sure to complete them?"
              : "Are you sure to cancelled them?"
          }`}
          okLoading={isBulkLoading}
        />
      )}

      {open?.type === "VIEW" && (
        <InterviewDetails
          open={open?.open}
          handleClose={handleClose}
          cv={(open?.data as TApplicationDetails)?.cv}
          title={(open?.data as TApplicationDetails)?.title}
          details={(open?.data as TApplicationDetails)?.details}
          id={(open?.data as TApplicationDetails)?.id}
          status={(open?.data as TApplicationDetails)?.status}
          offerStatus={(open?.data as TApplicationDetails)?.offerStatus}
          email={(open?.data as TApplicationDetails)?.email}
          phone={(open?.data as TApplicationDetails)?.phone}
        />
      )}

      <CustomTable
        loading={isLoading}
        limit={limit}
        page={page}
        columns={columns}
        total={data?.data?.totalLength}
        dataSource={addKeyInArray(data?.data?.interviews)}
        handleLimit={handleLimit}
        handlePagination={handlePagination}
        rowSelection={{
          type: "checkbox",
          selectedRowKeys: selectedData,
          onChange: (key) => {
            setSelectedData(key as string[]);
          },
          getCheckboxProps: (record) => {
            return {
              disabled: record?.status !== "pending",
            };
          },
          columnWidth: 40,
        }}
        filterSection={
          <>
            {selectedData?.length ? (
              <Select
                placeholder="Bulk Action"
                filterOption={false}
                options={selectStringOption(
                  ["completed", "cancelled"],
                  "string",
                  true
                )}
                className="min-w-[130px]"
                suffixIcon={<SelectDownIcon />}
                rootClassName="icon-gray"
                value={open?.data}
                onChange={(val) =>
                  handleOpen({
                    type: "CONFIRM_BULK_ACTION",
                    data: val,
                  })
                }
              />
            ) : null}
            <Select
              allowClear
              showSearch
              placeholder="Job Title"
              options={jobData?.data?.jobPosts?.map((job) => ({
                label: job?.job_title,
                value: job?.id,
              }))}
              onChange={(val) => handleFilter("job_post_id", val)}
              filterOption={selectSearchOption}
              className="min-w-[100px]"
              suffixIcon={<SelectDownIcon />}
              rootClassName="icon-gray"
              loading={isJobLoading}
              popupMatchSelectWidth={200}
            />
            <Select
              allowClear
              showSearch
              placeholder="Interview Type"
              options={Object.entries(IType).map(([value, label]) => ({
                label,
                value,
              }))}
              onChange={(val) => handleFilter("interview_type", val)}
              filterOption={selectSearchOption}
              className="min-w-[100px]"
              suffixIcon={<SelectDownIcon />}
              rootClassName="icon-gray"
              popupMatchSelectWidth={140}
            />
            <Select
              allowClear
              showSearch
              placeholder="Interview status"
              options={Object.entries(InterviewStatus).map(([value]) => ({
                label: capitalize(value),
                value,
              }))}
              onChange={(val) => handleFilter("offer_status", val)}
              filterOption={selectSearchOption}
              className="min-w-[110px]"
              suffixIcon={<SelectDownIcon />}
              rootClassName="icon-gray"
            />
            <Select
              allowClear
              showSearch
              placeholder="Status"
              options={Object.entries(Status).map(([value]) => ({
                label: capitalize(value),
                value,
              }))}
              onChange={(val) => handleFilter("status", val)}
              filterOption={selectSearchOption}
              className="min-w-[110px]"
              suffixIcon={<SelectDownIcon />}
              rootClassName="icon-gray"
            />
            <Link to="/admin/recruitment/interview-list/add">
              <Button
                icon={<AddIcon />}
                type="primary"
                className="sm-button !px-3"
              >
                Assign Interview
              </Button>
            </Link>
          </>
        }
      />
    </SidebarLayout>
  );
}
